/*
  MellowLune Design Layer - JS
  ------------------------------------------------------------------
  Native JS only, no third-party library, no dependency on Dawn's
  own component classes (custom elements are namespaced ml-*).
  Currently powers:
  - ml-countdown (sections/ml-countdown.liquid)
  - ml-video-slider (sections/ml-video-slider.liquid) - scroll-snap
    carousel nav only; video playback itself is Dawn's own
    <deferred-media> component (assets/global.js), reused as-is.
------------------------------------------------------------------- */

class MlCountdown extends HTMLElement {
  connectedCallback() {
    this.units = {};
    this.querySelectorAll('[data-ml-unit]').forEach((el) => {
      this.units[el.dataset.mlUnit] = el;
    });
    // Only the largest unit shown may exceed two digits, so the rest can
    // roll over into it (hours-only => 114 hours, not a hidden 4 days).
    this.order = ['days', 'hours', 'minutes', 'seconds'].filter((u) => this.units[u]);
    // A recurring campaign has already been rolled forward by Liquid, so it
    // arrives as an absolute epoch instead of a wall-clock date.
    this.endTime =
      this.dataset.useTs === 'true'
        ? parseInt(this.dataset.endTs, 10) * 1000
        : this.resolveEnd(this.dataset.end, this.dataset.timezone);

    this.removeAttribute('data-state');
    if (this.endTime === null || Number.isNaN(this.endTime)) {
      this.setEnded();
      return;
    }

    this.tick();
    this.interval = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    if (this.interval) clearInterval(this.interval);
  }

  /* "2026-12-31T23:59" read in a named IANA zone. Formatting the naive
     instant *in* that zone tells us how far off it is, DST included. */
  resolveEnd(value, timezone) {
    if (!value) return null;
    const parts = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{1,2}):(\d{2})/);
    if (!parts) return null;
    const [, y, mo, d, h, mi] = parts.map(Number);

    if (!timezone || timezone === 'local') {
      const local = new Date(y, mo - 1, d, h, mi, 0, 0);
      return Number.isNaN(local.getTime()) ? null : local.getTime();
    }

    const naive = Date.UTC(y, mo - 1, d, h, mi, 0, 0);
    try {
      const dtf = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      });
      const f = {};
      dtf.formatToParts(new Date(naive)).forEach((p) => {
        f[p.type] = p.value;
      });
      const seen = Date.UTC(+f.year, +f.month - 1, +f.day, +f.hour % 24, +f.minute, +f.second);
      return naive - (seen - naive);
    } catch (e) {
      return naive;
    }
  }

  tick() {
    const remaining = this.endTime - Date.now();
    if (remaining <= 0) {
      this.setEnded();
      return;
    }

    let total = Math.floor(remaining / 1000);
    const size = { days: 86400, hours: 3600, minutes: 60, seconds: 1 };
    this.order.forEach((unit) => {
      const value = Math.floor(total / size[unit]);
      total -= value * size[unit];
      this.units[unit].textContent = String(value).padStart(2, '0');
    });
  }

  setEnded() {
    if (this.interval) clearInterval(this.interval);
    this.setAttribute('data-state', 'ended');
    // "hide" removes the bar outright rather than leaving zeros on the page.
    // In the Theme Editor it stays put so the merchant can still select it.
    if (this.dataset.onExpiry === 'hide' && !(document.body && document.body.classList.contains('shopify-design-mode'))) {
      this.hidden = true;
    }
  }
}

if (!customElements.get('ml-countdown')) {
  customElements.define('ml-countdown', MlCountdown);
}

class MlVideoSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('.ml-videoslider__track');
    const wrapper = this.closest('.ml-videoslider');
    this.prevBtn = wrapper && wrapper.querySelector('.ml-videoslider__button--prev');
    this.nextBtn = wrapper && wrapper.querySelector('.ml-videoslider__button--next');

    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.scrollBySlide(-1));
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.scrollBySlide(1));
  }

  scrollBySlide(direction) {
    if (!this.track) return;
    const slide = this.track.querySelector('.ml-videoslider__slide');
    const amount = slide ? slide.getBoundingClientRect().width + 16 : this.track.clientWidth * 0.8;
    this.track.scrollBy({ left: amount * direction, behavior: 'smooth' });
  }
}

if (!customElements.get('ml-video-slider')) {
  customElements.define('ml-video-slider', MlVideoSlider);
}
